import {
  createMultiStyleConfigHelpers,
  MultiStyleType,
  StyleThemingProps,
} from '~/utils/chakraUtils'
import { mergeMultiStyleConfig } from '~/utils/styleHelpers'

import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'

// Layout Parts
const SettingsPageLayoutParts = [
  'tabListWrapper',
  'tabList',
  'header',
  'headerText',
  'version',
  'versionText',
] as const

// Tab Parts
const SettingsTabParts = [
  'container',
  'icon',
  'titleText',
  'badgeWrapper',
] as const

const layoutHelpers = createMultiStyleConfigHelpers(SettingsPageLayoutParts)
const tabHelpers = createMultiStyleConfigHelpers(SettingsTabParts)

// Layout Styles
export const SettingsPageLayoutStyle = layoutHelpers.defineMultiStyleConfig({
  baseStyle: {
    tabListWrapper: {
      display: 'flex',
      height: '100%',
      padding: '0 0.5rem',
      alignItems: 'flex-start',
      flex: '1 0 0',
      alignSelf: 'stretch',
      overflowY: 'auto',
    },
    tabList: {
      display: 'flex',
      flexDirection: 'column',
      bg: 'bg.default',
      padding: '0.75rem 0',
      alignItems: 'center',
      gap: '0.25rem',
      flex: '1 0 0',
      alignSelf: 'stretch',
    },
    header: {
      display: 'flex',
      width: '100%',
      height: '4.5rem',
      padding: '0 1rem',
      alignItems: 'center',
      gap: '0.25rem',
      borderBottom: '2px solid',
      borderColor: 'border.default',
    },
    headerText: {
      ...textStyles[textStyleKeys.Heading6],
      color: 'fg.default',
      fontSize: '16px',
      fontStyle: 'normal',
      fontWeight: '700',
      lineHeight: '24px',
    },
    version: {
      display: 'flex',
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center',
    },
    versionText: {
      color: 'fg.muted',
      fontWeight: 600,
      marginBottom: 4,
      fontSize: '0.8rem',
    },
  },
})

// Tab Styles
export const SettingsTabStyle = tabHelpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      display: 'flex',
      direction: 'row',
      width: '100%',
      minWidth: 0,
      overflow: 'hidden',
      padding: '0.75rem',
      borderRadius: '0.5rem',
      alignItems: 'center',
      cursor: 'pointer',
      userSelect: 'none',
      bg: 'bg.default',
      _hover: {
        bg: 'bg.muted',
      },
      _active: {
        bg: 'bg.muted',
      },
      _selected: {
        bg: 'bg.muted',
      },
    },
    icon: {
      boxSize: '1.375rem',
      color: 'fg.muted',
      _selected: {
        color: 'fg.info',
      },
    },
    titleText: {
      ...textStyles[textStyleKeys.Body2],
      color: 'fg.muted',
      marginLeft: '0.5rem',
      marginBottom: 0,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      minWidth: 0,
      _selected: {
        color: 'fg.info',
        ...textStyles[textStyleKeys.Body2_Semibold],
      },
    },
    badgeWrapper: {
      display: 'flex',
      alignItems: 'center',
    },
  },
})

// Hooks
export const useSettingsPageLayoutStyle = (props?: StyleThemingProps) => {
  return mergeMultiStyleConfig(
    SettingsPageLayoutStyle,
    props,
  ) as MultiStyleType<typeof SettingsPageLayoutParts>
}

export const useSettingsTabStyle = (props?: StyleThemingProps) => {
  return mergeMultiStyleConfig(SettingsTabStyle, props) as MultiStyleType<
    typeof SettingsTabParts
  >
}
